import React, { useEffect, useState } from "react";
import { api, formatErr, thumbUrl, API } from "../lib/api";
import { toast } from "sonner";
import { Trash, UploadSimple, Users, Barbell, CurrencyGbp, Repeat, Heart, VideoCamera } from "@phosphor-icons/react";

const CATEGORIES = ["Strength", "Cardio", "HIIT", "Recovery", "Mobility", "Combat"];
const DIFFICULTIES = ["Beginner", "Intermediate", "Advanced"];

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [classes, setClasses] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [reels, setReels] = useState([]);
  const [form, setForm] = useState({
    title: "", description: "", category: "Strength", difficulty: "Intermediate",
    duration_min: 30, is_free: false,
  });
  const [thumb, setThumb] = useState(null);
  const [video, setVideo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [reelFile, setReelFile] = useState(null);
  const [reelTitle, setReelTitle] = useState("");
  const [reelUploading, setReelUploading] = useState(false);
  const [reelProgress, setReelProgress] = useState(0);

  const refresh = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/classes").then((r) => setClasses(r.data)).catch(() => {});
    api.get("/paypal/config").then((r) => setCfg(r.data)).catch(() => {});
    api.get("/reels").then((r) => setReels(r.data)).catch(() => {});
  };
  useEffect(refresh, []);

  const uploadReel = async (e) => {
    e.preventDefault();
    if (!reelFile) return toast.error("Pick a video first");
    const mb = reelFile.size / (1024 * 1024);
    if (mb > 200) {
      if (!window.confirm(`Reel is ${mb.toFixed(0)} MB — large reels may time out. Continue?`)) return;
    }
    const fd = new FormData();
    fd.append("title", reelTitle);
    fd.append("video", reelFile);
    setReelUploading(true);
    setReelProgress(0);
    try {
      await api.post("/admin/reels", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 30 * 60 * 1000,
        onUploadProgress: (evt) => {
          if (evt.total) setReelProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      toast.success("Reel uploaded");
      setReelTitle(""); setReelFile(null);
      document.getElementById("reel-input").value = "";
      refresh();
    } catch (e) {
      toast.error(formatErr(e));
    } finally { setReelUploading(false); setReelProgress(0); }
  };

  const deleteReel = async (id) => {
    if (!window.confirm("Delete this reel?")) return;
    try {
      await api.delete(`/admin/reels/${id}`);
      toast.success("Deleted");
      refresh();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!thumb || !video) return toast.error("Thumbnail and video are required");
    const videoMB = video.size / (1024 * 1024);
    setUploading(true);
    setProgress(0);

    // Wake Lock — keep screen awake during upload (iOS 16.4+, Android)
    let wakeLock = null;
    if ("wakeLock" in navigator) {
      try { wakeLock = await navigator.wakeLock.request("screen"); } catch (_err) { /* ignore */ }
    }

    // File fingerprint — to detect resume of the same file
    const fingerprint = `${video.name}|${video.size}|${video.lastModified}`;
    const stored = JSON.parse(localStorage.getItem("wp_upload_session") || "null");

    try {
      // 1. Thumbnail to base64
      const thumbB64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(thumb);
      });

      // 2. Resume existing session if file matches, otherwise init new
      let uploadId;
      let uploadedIdx = new Set();
      if (stored && stored.fingerprint === fingerprint && stored.upload_id) {
        uploadId = stored.upload_id;
        try {
          const st = await api.get(`/admin/upload/status?upload_id=${uploadId}`);
          if (st.data.exists) {
            uploadedIdx = new Set(st.data.uploaded_indexes);
            toast.success(`Resuming upload — ${uploadedIdx.size} chunks already uploaded`);
          }
        } catch (_) { /* fall through and re-init */ }
      }
      if (!uploadId || uploadedIdx.size === 0 && !(stored && stored.fingerprint === fingerprint)) {
        const initRes = await api.post("/admin/upload/init", { upload_id: uploadId });
        uploadId = initRes.data.upload_id;
      }

      // 3. Upload chunks (2MB each — better mobile reliability)
      const CHUNK = 2 * 1024 * 1024;
      const total = Math.ceil(video.size / CHUNK);

      localStorage.setItem("wp_upload_session", JSON.stringify({ fingerprint, upload_id: uploadId, total }));

      for (let i = 0; i < total; i++) {
        if (uploadedIdx.has(i)) {
          setProgress(Math.round(((i + 1) / total) * 95));
          continue;
        }
        const start = i * CHUNK;
        const end = Math.min(start + CHUNK, video.size);
        const slice = video.slice(start, end);
        const cfd = new FormData();
        cfd.append("upload_id", uploadId);
        cfd.append("index", i);
        cfd.append("chunk", slice, `chunk_${i}`);
        let attempt = 0;
        while (true) {
          try {
            await api.post("/admin/upload/chunk", cfd, { timeout: 5 * 60 * 1000 });
            break;
          } catch (err) {
            attempt += 1;
            if (attempt >= 5) throw err;
            await new Promise((r) => setTimeout(r, 2000 * attempt)); // backoff
          }
        }
        setProgress(Math.round(((i + 1) / total) * 95));
      }

      // 4. Complete upload
      const ext = (video.name.split(".").pop() || "mp4").toLowerCase();
      const completeRes = await api.post("/admin/upload/complete", {
        upload_id: uploadId,
        total_chunks: total,
        ext,
        content_type: video.type || "video/mp4",
        kind: "video",
      }, { timeout: 15 * 60 * 1000 });

      // 5. Create class
      const thumbExt = (thumb.name.split(".").pop() || "jpg").toLowerCase();
      await api.post("/admin/classes/from_upload", {
        title: form.title,
        description: form.description,
        category: form.category,
        difficulty: form.difficulty,
        duration_min: form.duration_min,
        is_free: form.is_free,
        video_path: completeRes.data.storage_path,
        video_content_type: completeRes.data.content_type,
        thumbnail_b64: thumbB64,
        thumbnail_ext: thumbExt,
        thumbnail_content_type: thumb.type || "image/jpeg",
      });
      setProgress(100);

      localStorage.removeItem("wp_upload_session");
      toast.success("Class uploaded");
      setForm({ title: "", description: "", category: "Strength", difficulty: "Intermediate", duration_min: 30, is_free: false });
      setThumb(null); setVideo(null);
      document.getElementById("thumb-input").value = "";
      document.getElementById("video-input").value = "";
      refresh();
    } catch (e) {
      const msg = e.code === "ECONNABORTED" || e.message?.includes("Network")
        ? `Upload paused (${videoMB.toFixed(0)} MB). Tap "Publish Class" again with the same video to resume from where it stopped.`
        : formatErr(e);
      toast.error(msg, { duration: 8000 });
    }
    finally {
      setUploading(false); setProgress(0);
      if (wakeLock) { try { await wakeLock.release(); } catch (_err) { /* ignore */ } }
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this class?")) return;
    try {
      await api.delete(`/admin/classes/${id}`);
      toast.success("Deleted");
      refresh();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const singlePrice = cfg?.single_class_price || "4.00";

  return (
    <div className="max-w-7xl mx-auto px-6 py-12" data-testid="admin-page">
      <span className="label-overline">// Control Room</span>
      <h1 className="font-display text-5xl tracking-widest leading-none mt-3 mb-10">Admin Dashboard</h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10" data-testid="stats-grid">
        {[
          { icon: Users, label: "Users", value: stats?.total_users ?? "—" },
          { icon: Barbell, label: "Classes", value: stats?.total_classes ?? "—" },
          { icon: CurrencyGbp, label: "Revenue (one-time)", value: stats ? `£${stats.revenue_one_time}` : "—" },
          { icon: Repeat, label: "Active Subs", value: stats?.active_subscriptions ?? "—" },
        ].map((s, i) => (
          <div key={i} className="border border-white/10 p-5 h-full" data-testid={`stat-${i}`}>
            <s.icon size={20} weight="duotone" className="text-[#D4A574]" />
            <div className="label-overline mt-3">{s.label}</div>
            <div className="font-display text-4xl mt-1">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        <form onSubmit={submit} className="border border-white/10 p-6 space-y-4" data-testid="upload-form">
          <h2 className="font-display text-3xl tracking-wider mb-2 flex items-center gap-2"><UploadSimple weight="duotone" /> Upload Class</h2>
          <p className="text-xs text-zinc-500 -mt-2">Single-class price is fixed at <span className="text-[#D4A574] font-bold">£{Number(singlePrice).toFixed(2)}</span> (set in backend env).</p>

          <div>
            <label className="label-overline block mb-1">Title</label>
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full bg-[#2A2018] border border-white/15 px-3 py-2 focus:border-[#D4A574] outline-none" data-testid="form-title" />
          </div>
          <div>
            <label className="label-overline block mb-1">Description</label>
            <textarea required rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full bg-[#2A2018] border border-white/15 px-3 py-2 focus:border-[#D4A574] outline-none" data-testid="form-description" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-overline block mb-1">Category</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full bg-[#2A2018] border border-white/15 px-3 py-2" data-testid="form-category">
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="label-overline block mb-1">Difficulty</label>
              <select value={form.difficulty} onChange={(e) => setForm({ ...form, difficulty: e.target.value })}
                className="w-full bg-[#2A2018] border border-white/15 px-3 py-2" data-testid="form-difficulty">
                {DIFFICULTIES.map((d) => <option key={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="label-overline block mb-1">Duration (min)</label>
              <input type="number" min={1} required value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: +e.target.value })}
                className="w-full bg-[#2A2018] border border-white/15 px-3 py-2" data-testid="form-duration" />
            </div>
            <label className="flex items-center gap-3 self-end pb-2 cursor-pointer select-none" data-testid="form-is-free-label">
              <input type="checkbox" checked={form.is_free} onChange={(e) => setForm({ ...form, is_free: e.target.checked })}
                className="w-5 h-5 accent-[#D4A574]" data-testid="form-is-free" />
              <span className="label-overline flex items-center gap-1"><Heart weight="fill" size={14} className="text-[#D4A574]" /> Free class</span>
            </label>
          </div>
          <div>
            <label className="label-overline block mb-1">Thumbnail (JPG/PNG)</label>
            <input id="thumb-input" type="file" accept="image/*" required onChange={(e) => setThumb(e.target.files[0])}
              className="w-full text-sm text-zinc-300 file:mr-3 file:border-0 file:bg-[#D4A574] file:text-black file:px-3 file:py-1.5 file:font-bold file:uppercase file:text-xs file:tracking-widest"
              data-testid="form-thumb" />
          </div>
          <div>
            <label className="label-overline block mb-1">Video (MP4)</label>
            <input id="video-input" type="file" accept="video/*" required onChange={(e) => setVideo(e.target.files[0])}
              className="w-full text-sm text-zinc-300 file:mr-3 file:border-0 file:bg-[#D4A574] file:text-black file:px-3 file:py-1.5 file:font-bold file:uppercase file:text-xs file:tracking-widest"
              data-testid="form-video" />
            {video && (
              <p className="text-xs mt-2 text-zinc-400" data-testid="video-size">
                {(video.size / (1024 * 1024)).toFixed(1)} MB
                {video.size / (1024 * 1024) > 500 && <span className="ml-2 text-zinc-500">— large file, upload may take several minutes</span>}
              </p>
            )}
          </div>
          {uploading && (
            <div className="space-y-2" data-testid="upload-progress">
              <div className="flex justify-between label-overline">
                <span>Uploading…</span>
                <span className="text-[#D4A574]">{progress}%</span>
              </div>
              <div className="h-1 bg-white/10 overflow-hidden">
                <div className="h-full bg-[#D4A574] transition-all duration-200" style={{ width: `${progress}%` }} />
              </div>
              <p className="text-xs text-zinc-500">Keep this tab open and your screen on. If it fails, tap Publish again — it&apos;ll resume where it stopped.</p>
            </div>
          )}
          <button disabled={uploading} className="kinetic-btn w-full" data-testid="upload-submit-btn">
            {uploading ? `Uploading ${progress}%…` : "Publish Class"}
          </button>
        </form>

        <div className="border border-white/10 p-6" data-testid="admin-class-list">
          <h2 className="font-display text-3xl tracking-wider mb-5">Published Classes</h2>
          {classes.length === 0 ? (
            <p className="text-zinc-500 text-sm">No classes yet.</p>
          ) : (
            <ul className="space-y-3">
              {classes.map((c) => (
                <li key={c.id} className="flex items-center gap-4 border border-white/5 p-3" data-testid={`admin-class-${c.id}`}>
                  <img src={thumbUrl(c.id)} alt="" className="w-20 h-12 object-cover" onError={(e) => e.target.style.display = "none"} />
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-lg tracking-wider truncate flex items-center gap-2">
                      {c.title}
                      {c.is_free && <span className="label-overline bg-[#D4A574] text-black px-1.5 py-0.5 text-[10px]">Free</span>}
                    </div>
                    <div className="text-xs text-zinc-500">{c.is_free ? "£0" : `£${Number(c.price ?? 4).toFixed(2)}`} · {c.category} · {c.duration_min}m</div>
                  </div>
                  <button onClick={() => del(c.id)} className="text-zinc-400 hover:text-[#FF3B30]" data-testid={`delete-class-${c.id}`}>
                    <Trash size={18} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Reels manager */}
      <div className="mt-10 grid lg:grid-cols-2 gap-8" data-testid="reels-section">
        <form onSubmit={uploadReel} className="border border-white/10 p-6 space-y-4" data-testid="reel-upload-form">
          <h2 className="font-display text-3xl tracking-wider mb-2 flex items-center gap-2"><VideoCamera weight="duotone" /> Add a Reel</h2>
          <p className="text-xs text-zinc-500 -mt-2">Short vertical clips (5–30s, ideally &lt; 30 MB) shown on the homepage. They auto-play muted.</p>

          <div>
            <label className="label-overline block mb-1">Title (optional)</label>
            <input value={reelTitle} onChange={(e) => setReelTitle(e.target.value)}
              placeholder="e.g. Cable pull-down"
              className="w-full bg-[#2A2018] border border-white/15 px-3 py-2 focus:border-[#D4A574] outline-none" data-testid="reel-title" />
          </div>

          <div>
            <label className="label-overline block mb-1">Video (vertical 9:16)</label>
            <input id="reel-input" type="file" accept="video/*" required onChange={(e) => setReelFile(e.target.files[0])}
              className="w-full text-sm text-zinc-300 file:mr-3 file:border-0 file:bg-[#D4A574] file:text-black file:px-3 file:py-1.5 file:font-bold file:uppercase file:text-xs file:tracking-widest"
              data-testid="reel-file" />
            {reelFile && (
              <p className={`text-xs mt-2 ${reelFile.size / (1024 * 1024) > 30 ? "text-[#FF3B30]" : "text-zinc-400"}`}>
                {(reelFile.size / (1024 * 1024)).toFixed(1)} MB
                {reelFile.size / (1024 * 1024) > 30 && " — consider compressing"}
              </p>
            )}
          </div>

          {reelUploading && (
            <div className="space-y-2">
              <div className="flex justify-between label-overline">
                <span>Uploading…</span>
                <span className="text-[#D4A574]">{reelProgress}%</span>
              </div>
              <div className="h-1 bg-white/10 overflow-hidden">
                <div className="h-full bg-[#D4A574] transition-all duration-200" style={{ width: `${reelProgress}%` }} />
              </div>
            </div>
          )}

          <button disabled={reelUploading} className="kinetic-btn w-full" data-testid="reel-submit-btn">
            {reelUploading ? `Uploading ${reelProgress}%…` : "Publish Reel"}
          </button>
        </form>

        <div className="border border-white/10 p-6" data-testid="reel-list">
          <h2 className="font-display text-3xl tracking-wider mb-5">Published Reels</h2>
          {reels.length === 0 ? (
            <p className="text-zinc-500 text-sm">No reels yet. Add your first short clip on the left.</p>
          ) : (
            <div className="grid grid-cols-3 gap-3">
              {reels.map((r) => (
                <div key={r.id} className="relative aspect-[9/16] bg-black overflow-hidden group" data-testid={`admin-reel-${r.id}`}>
                  <video src={`${API}/media/reel/${r.id}`} muted loop playsInline preload="metadata" className="w-full h-full object-cover" onMouseEnter={(e) => e.target.play().catch(() => {})} onMouseLeave={(e) => e.target.pause()} />
                  {r.title && <div className="absolute bottom-0 left-0 right-0 p-1.5 text-[10px] text-white bg-black/60 truncate">{r.title}</div>}
                  <button onClick={() => deleteReel(r.id)} className="absolute top-1.5 right-1.5 bg-black/70 backdrop-blur p-1.5 text-zinc-200 hover:text-[#FF3B30]" data-testid={`delete-reel-${r.id}`}>
                    <Trash size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
